import { ClipboardCopyIcon } from '@radix-ui/react-icons';
import { Badge, Flex, Text } from '@radix-ui/themes';
import { NotifyHelper } from 'classes/helpers/notify.helper';
import { FlexTableWrapper } from 'components/common/layout/flex-table-wrapper';
import { CommonTableHoC } from 'components/common/table';
import { CommonTooltip } from 'components/common/tooltip';
import { formatDate } from 'components/sections/admin-portal/machines/dialogs/settings/machine-errors/common';
import { MissedDetectionsHistoryToolbar } from 'components/sections/admin-portal/machines/dialogs/settings/machine-shot-history/missed-vision-detections/missed-detections-history-toolbar';
import { IMenuAction } from 'interfaces/i-menus';
import { ITableCheckable } from 'interfaces/tables/checking';
import { ITypedTableColumn } from 'interfaces/tables/columns';
import { ITableInfiniteLoader } from 'interfaces/tables/infinite-loader';
import { ITableSelectable } from 'interfaces/tables/selection';
import { RADIX } from 'lib_ts/enums/radix-ui';
import { IMissedVisionDetection } from 'lib_ts/interfaces/vision/i-vision-detection';
import { useMemo } from 'react';

interface IProps {
  hasMore: boolean;
  missedDetections: IMissedVisionDetection[];
  newMissedDetectionID: string;
  updatedMissedDetectionID: string;
  loading: boolean;
  onLoadMore: () => void;
  onSelect: (vd: IMissedVisionDetection) => void;
}

export const MissedDetectionsHistoryTable = (props: IProps) => {
  const { missedDetections, loading, hasMore, onSelect, onLoadMore } = props;

  const columns = useMemo<ITypedTableColumn<IMissedVisionDetection>[]>(
    () => [
      {
        label: 'Date',
        key: '_created',
        formatFn: (v) => <Text size="1">{formatDate(v._created)}</Text>,
      },
      {
        label: 'Pitch Name',
        key: 'pitchName',
        formatFn: (v) => (
          <Badge
            color={v.pitch?.name ? RADIX.COLOR.ACCENT : RADIX.COLOR.SECONDARY}
          >
            {v.pitch?.name || 'N/A'}
          </Badge>
        ),
      },
      {
        label: 'Message',
        key: 'message',
        formatFn: (v) => (
          <CommonTooltip
            text={v.message ?? '-'}
            trigger={<Text>{v.message ?? '-'}</Text>}
            disabled={!v.message}
          />
        ),
      },
    ],
    []
  );

  const tableSelection: ITableSelectable = {
    enableSelect: true,
    afterChangeSelected: onSelect,
  };

  const getCheckedMenuActions = (
    checked: IMissedVisionDetection[]
  ): IMenuAction[] => [
    {
      label: 'Copy fire IDs',
      prefixIcon: <ClipboardCopyIcon />,
      onClick: () => {
        const ids = checked.map((s) => s.fireEventID).join(', ');
        navigator.clipboard.writeText(ids);

        const message_md =
          checked.length > 1
            ? `Copied ${checked.length} fire IDs to clipboard`
            : `Copied Fire ID ${ids} to clipboard`;

        NotifyHelper.success({
          message_md,
        });
      },
    },
    {
      label: 'Copy missed detection IDs',
      prefixIcon: <ClipboardCopyIcon />,
      onClick: () => {
        const ids = checked.map((s) => s._id).join(', ');
        navigator.clipboard.writeText(ids);

        const message_md =
          checked.length > 1
            ? `Copied ${checked.length} missed detection IDs to clipboard`
            : `Copied Missed Detection ID ${ids} to clipboard`;

        NotifyHelper.success({
          message_md,
        });
      },
    },
  ];

  const checkable: ITableCheckable = {
    checkboxColumnIndex: 0,
    checkedActions: getCheckedMenuActions,
  };

  const tableInfiniteScrollable: ITableInfiniteLoader = {
    onLoadMore,
    hasMore,
    rootMargin: '128px',
  };

  return (
    <Flex
      direction="column"
      gap={RADIX.FLEX.GAP.MD}
      className={RADIX.VFLEX.GROW}
    >
      <FlexTableWrapper
        gap={RADIX.FLEX.GAP.FORM}
        header={<MissedDetectionsHistoryToolbar />}
        table={
          <CommonTableHoC
            dataTestID="MissedDetectionsHistoryTable"
            identifier="MissedDetectionsHistoryTable"
            displayColumns={columns}
            displayData={missedDetections}
            loading={loading}
            rowClassNameFn={(s: IMissedVisionDetection) => {
              if (!s) {
                return '';
              }

              if (s._id === props.updatedMissedDetectionID) {
                return 'table-updated-row';
              }

              if (s._id === props.newMissedDetectionID) {
                return 'table-new-row';
              }

              return '';
            }}
            {...tableSelection}
            {...checkable}
            {...tableInfiniteScrollable}
            vFlex
            skipResetSelected
          />
        }
      />
    </Flex>
  );
};
